import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, StripeCardElementChangeEvent } from "@stripe/stripe-js";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import SquareTile from "src/components/generic/tiles/SquareTile";
import { Button, LoadingSpinner } from "src/components/generic";
import { stripe as stripeUtils } from "src/utils";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_ACCESS_KEY || "");

interface ICardManagementProps {
  customerId: string;
};

type DefaultCardView = {
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
};

const CardManagement: FunctionComponent<ICardManagementProps> = ({ customerId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [defaultCard, setDefaultCard] = useState<DefaultCardView | null>(null);

  const elementsOptions = useMemo(() => {
    if (!clientSecret) return undefined;
    return { clientSecret };
  }, [clientSecret]);

  const refresh = async () => {
    if (!customerId) return;

    setError("");
    setLoading(true);
    try {
      // 1) Try to fetch current default payment method (may error; we'll tolerate)
      try {
        const pmRes = await stripeUtils.payments.fetchCustomerPaymentMethod(customerId);
        const pm = pmRes?.paymentMethod;

        if (pm?.card) {
          setDefaultCard({
            brand: pm.card.brand,
            last4: pm.card.last4,
            exp_month: pm.card.exp_month,
            exp_year: pm.card.exp_year,
          });
        } else {
          setDefaultCard(null);
        }
      } catch {
        // Backend sometimes 500s; treat as "no default card"
        setDefaultCard(null);
      }

      // 2) Always create a setup intent so the tile can add/change card
      const si = await stripeUtils.payments.createSetupIntentForSubscription(customerId);
      const secret = si?.clientSecret || si?.client_secret;
      if (!secret) throw new Error("Missing client secret from SetupIntent");

      setClientSecret(secret);
    } catch (e: any) {
      setError(e?.message || "Failed to load card management.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  return (
    <SquareTile title="Card Management">
      {loading && <LoadingSpinner />}
      {!loading && error && <p className="text-red-600">{error}</p>}

      {!loading && !error && (
        <>
          <div className="mb-4">
            {defaultCard?.last4 ? (
              <div className="space-y-1">
                <p className="text-sm opacity-80">Default card</p>
                <p className="font-medium">
                  {defaultCard.brand?.toUpperCase()} •••• {defaultCard.last4}
                </p>
                {(defaultCard.exp_month && defaultCard.exp_year) && (
                  <p className="text-sm opacity-80">
                    Expires {defaultCard.exp_month}/{defaultCard.exp_year}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm opacity-80">No default card on file.</p>
            )}
          </div>

          {clientSecret && elementsOptions ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CardManagementInner
                customerId={customerId}
                hasDefaultCard={!!defaultCard?.last4}
                clientSecret={clientSecret}
                onChanged={refresh}
              />
            </Elements>
          ) : (
            <div className="space-y-3">
              <p>Unable to initialize Stripe card form.</p>
              <Button text="Retry" onClick={refresh} />
            </div>
          )}
        </>
      )}
    </SquareTile>
  );
};

export default CardManagement;

const CardManagementInner: FunctionComponent<{
  customerId: string;
  hasDefaultCard: boolean;
  clientSecret: string;
  onChanged: () => Promise<void>;  
}> = ({ customerId, hasDefaultCard, clientSecret, onChanged }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [complete, setComplete] = useState(false);

  const onCardChange = (e: StripeCardElementChangeEvent) => {
    setComplete(e.complete);
    if (e.error?.message) setErr(e.error.message);
    else setErr("");
  };

  /**
   * IMPORTANT:
   * Some Stripe versions require you to pass the clientSecret explicitly to confirmCardSetup.
   * Since you are initializing Elements with clientSecret, we can still confirm using:
   *   stripe.confirmCardSetup(clientSecret, ...)
   *
   * If you get "client_secret required" here, pass the clientSecret as a prop to this inner component.
   */
  const confirmCardAndGetPaymentMethodId = async (): Promise<string> => {
    if (!stripe || !elements) throw new Error("Stripe is not ready.");
    const card = elements.getElement(CardElement);
    if (!card) throw new Error("Card element not found.");

    // ⚠️ If this errors in your Stripe version, pass clientSecret into this component and use it explicitly.
    const result = await stripe.confirmCardSetup(clientSecret, { payment_method: { card } } as any);

    if (result.error) throw new Error(result.error.message || "Card setup failed.");
    const pm = (result.setupIntent as any)?.payment_method;
    if (!pm) throw new Error("Missing payment_method on SetupIntent.");
    return typeof pm === "string" ? pm : pm.id;
  };

  const handleSaveDefault = async () => {
    setBusy(true);
    setErr("");
    setMsg("");

    try {
      if (!complete) throw new Error("Please complete card details first.");

      const paymentMethodId = await confirmCardAndGetPaymentMethodId();

      await stripeUtils.payments.updatePaymentMethod(customerId, paymentMethodId);

      setMsg(hasDefaultCard ? "Default card updated." : "Default card set.");
      await onChanged();
    } catch (e: any) {
      setErr(e?.message || "Failed to save card.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded border p-3">
        <CardElement onChange={onCardChange} options={{ hidePostalCode: true }} />
      </div>

      <Button
        text={busy ? "Saving..." : hasDefaultCard ? "Change default card" : "Set default card"}
        disabled={busy || !complete}
        styles="w-[200px]"
        onClick={handleSaveDefault}
      />

      {err && <p className="text-red-600">{err}</p>}
      {msg && <p className="text-green-700">{msg}</p>}

      <p className="text-xs opacity-70">
        Your payment details are securely stored by Stripe.
      </p>
    </div>
  );
};
